// shared/combat — secret: damage / resist / true-damage formulas.

import { z } from 'zod';
import { RESIST_CAP, CRIT_CHANCE, CRIT_MULTIPLIER, VARIANCE } from './data.js';

export const DamageTypeSchema = z.enum(['Physical', 'Magic', 'True']);
export type DamageType = z.infer<typeof DamageTypeSchema>;

export interface Combatant {
  hp: number;
  hpMax: number;
  resistances: Partial<Record<DamageType, number>>;
}

export interface AttackSpec {
  baseDamage: number;
  damageType: DamageType;
}

export interface CombatContext {
  now: number;
  rng: () => number;
}

export interface CombatResult {
  damageDealt: number;
  damageType: DamageType;
  killed: boolean;
  crit: boolean;
  newDefenderHp: number;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export function resolve(
  defender: Combatant,
  attack: AttackSpec,
  ctx: CombatContext
): CombatResult {
  if (attack.baseDamage <= 0) {
    return {
      damageDealt: 0,
      damageType: attack.damageType,
      killed: false,
      crit: false,
      newDefenderHp: defender.hp,
    };
  }

  const variance = 1 + (ctx.rng() * 2 - 1) * VARIANCE;
  let dmg = attack.baseDamage * variance;

  const crit = ctx.rng() < CRIT_CHANCE;
  if (crit) dmg *= CRIT_MULTIPLIER;

  if (attack.damageType !== 'True') {
    const r = clamp(defender.resistances[attack.damageType] ?? 0, 0, RESIST_CAP);
    dmg *= 1 - r;
  }

  const damageDealt = Math.max(0, Math.round(dmg));
  const newHp = Math.max(0, defender.hp - damageDealt);

  return {
    damageDealt,
    damageType: attack.damageType,
    killed: newHp <= 0 && defender.hp > 0,
    crit,
    newDefenderHp: newHp,
  };
}
